import axios from 'axios';

// this base url will be change based on
// if you need to point to production.
const BASE_URL = '/api';
const ACCESS_TOKEN = 'access_token';
const REFRESH_TOKEN = 'refresh_token';

const tokenRequest = axios.create({
    baseURL: BASE_URL,
    timeout: 5000,
    headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
    },
});

const anonymousRequest = axios.create({
    baseURL: BASE_URL,
    timeout: 5000,
    headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
    },
});

const authRequest = axios.create({
    baseURL: BASE_URL,
    timeout: 5000,
    headers: {
        Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
        'Content-Type': 'application/json',
    },
});

const loginUser = (username, password) => {
    const loginBody = { username, password };
    return tokenRequest.post('/token/', loginBody)
        .then((response) => {
            window.localStorage.setItem(ACCESS_TOKEN, response.data.access);
            window.localStorage.setItem(REFRESH_TOKEN, response.data.refresh);
            authRequest.defaults.headers.Authorization = `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`;
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

const signUpUser = (username, password, firstName, lastName) => {
    const signUpBody = { 
        username: username,
        email: username,
        password: password,
        firstName: firstName,
        lastName: lastName

    };
    return tokenRequest.post('/users/', signUpBody)
        .then((response) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

const refreshToken = () => {
    const refreshBody = { refresh: window.localStorage.getItem(REFRESH_TOKEN) };
    return tokenRequest.post('/token/refresh/', refreshBody)
        .then((response) => {
            window.localStorage.setItem(ACCESS_TOKEN, response.data.access);
            authRequest.defaults.headers.Authorization = `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`;
            return Promise.resolve(response.data);
        })
        .catch((error) => Promise.reject(error));
};

const isCorrectRefreshError = (status) => status === 401;

/*
 * authRequest
 *
 * This refreshes the request and retries the token if it is invalid.
 * This is what you use to create any requests that need the Tokens.
 * Reference: https://hackernoon.com/110percent-complete-jwt-authentication-with-django-and-react-2020-iejq34ta
 *
 * Example:
 *     authRequest.get('/path/to/endpoint/',extraParameters)
 *        .then(response=>{
 *          // do something with successful request
 *        }).catch((error)=> {
 *          // handle any errors.
 *        });
*/


const logoutUser = () => {
    window.localStorage.removeItem(ACCESS_TOKEN);
    window.localStorage.removeItem(REFRESH_TOKEN);
    authRequest.defaults.headers.Authorization = '';
};

const errorInterceptor = (error) => {
    const originalRequest = error.config;
    const { status } = error.response;
    if (isCorrectRefreshError(status)) {
        return refreshToken().then(() => {
            const headerAuthorization = `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`;
            authRequest.defaults.headers.Authorization = headerAuthorization;
            originalRequest.headers.Authorization = headerAuthorization;
            return authRequest(originalRequest);
        }).catch((tokenRefreshError) => {
            // if token refresh fails, logout the user to avoid potential security risks.
            logoutUser();
            return Promise.reject(tokenRefreshError);
        });
    }
    return Promise.reject(error);
};

authRequest.interceptors.response.use(
    (response) => response, // this is for all successful requests.
    (error) => errorInterceptor(error), // handle the request
);

const resendActivationEmail = async (email) => {
    return anonymousRequest.post('users/resend_activation/', {
        email: email
    })
    .then((response) => { return Promise.resolve(response.data); })
    .catch((error) => { return Promise.reject(error); });
}

const activateAccount = async (uid, token) => {
    return anonymousRequest.post('/users/activation/', {
        uid: uid,
        token: token
    })
    .then((response) => {
        return Promise.resolve(response.data);
    })
    .catch((error) => {
        return Promise.reject(error);
    });
}

const resetPassword = async (email) => {
    return anonymousRequest.post('users/reset_password/', {
        email: email
    })
    .then((response) => { return Promise.resolve(response.data); })
    .catch((error) => { return Promise.reject(error); });
}

const resetPasswordConfirm = async (uid, token, password) => {
    return anonymousRequest.post('/users/reset_password_confirm/', {
        uid: uid,
        token: token,
        newPassword: password,
    })
    .then((response) => {
        return Promise.resolve(response.data);
    })
    .catch((error) => {
        return Promise.reject(error);
    });
}

const changePassword = async (currentPassword, newPassword) => {
    return authRequest.post('/users/set_password/', {
        currentPassword: currentPassword,
        newPassword: newPassword,
    })
    .then((response) => {
        return Promise.resolve(response.data);
    })
    .catch((error) => {
        return Promise.reject(error);
    });
}

const changeEmail = async (currentPassword, email) => {
    return authRequest.post('/users/set_username/', {
        currentPassword: currentPassword,
        newUsername: email,
    })
    .then((response) => {
        return Promise.resolve(response.data);
    })
    .catch((error) => {
        return Promise.reject(error);
    });
}

export {
    tokenRequest, anonymousRequest, loginUser, signUpUser, logoutUser, refreshToken, authRequest, changePassword, changeEmail,
    errorInterceptor, activateAccount, resendActivationEmail, resetPassword, resetPasswordConfirm, BASE_URL, ACCESS_TOKEN, REFRESH_TOKEN,
};